/**
 * useIsDesk - is this a computer-sized window? (V19.19)
 *
 * THE ONE RULE: THE PHONE NEVER CHANGES.
 *
 *   Every computer-only piece of the app sits behind this hook (and the one
 *   "ON A COMPUTER" CSS block in public/index.html, which uses the same
 *   1024 px line). Below 1024 px this returns false and the app renders
 *   exactly what it rendered before the computer layout existed.
 *
 *   1024 px, not 768: an iPad held sideways is 1024+ and gets the computer
 *   layout; an iPad upright (768-834) and every phone keep the phone layout.
 *
 *   The same media query string is exported so the CSS and the JS can never
 *   disagree about where the line is.
 */
import { useEffect, useState } from "react";

export const DESK_QUERY = "(min-width: 1024px)";

export const isDeskNow = () => {
  try {
    return typeof window !== "undefined" && !!window.matchMedia &&
      window.matchMedia(DESK_QUERY).matches;
  } catch {
    return false;
  }
};

export function useIsDesk() {
  const [desk, setDesk] = useState(isDeskNow);
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia(DESK_QUERY);
    const on = () => setDesk(mq.matches);
    on();
    if (mq.addEventListener) mq.addEventListener("change", on);
    else if (mq.addListener) mq.addListener(on);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", on);
      else if (mq.removeListener) mq.removeListener(on);
    };
  }, []);
  return desk;
}

export default useIsDesk;
